import { User } from "@auth0/auth0-react";
import { Action } from "@reduxjs/toolkit";
import { LoginState } from "../reducers/LoginReducer";
import { RootState } from "../reducers/RootReducer";
import { getInitialAuctionData } from "./FreeAgentActions";
import GeneralApiSvc from "../../services/GeneralApiSvc";
import { loadDashboardData } from "./TransactionActions";
import { updateOverUnders } from "./OverUnderActions";
import { LEAGUE_PREF_KEY } from "../../components/menu/LeagueSwitchMenu";
import { ChatClient } from "../../services/ChatUtils";
import { setChatConnected } from "../reducers/ChatReducer";

export const UPDATE_LOGIN = "UPDATE_LOGIN";

export interface LoginAction extends Action {
  payload: LoginState;
}

export const updateLoginInfo = (profile: LoginState): LoginAction => {
  return {
    type: UPDATE_LOGIN,
    payload: profile,
  };
};

export const synchronizeAuth0WithDbLogin =
  (user: User) => async (dispatch: Function, getState: () => RootState) => {
    const { profile } = getState();
    const { overUnders } = getState();

    console.log("[synchronizeAuth0WithDbLogin] Calling API...");
    try {
      const dbUser = await GeneralApiSvc.synchronizeAuth(user);
      console.log("[synchronizeAuth0WithDbLogin] API returned dbUser:", dbUser);

      var pool = dbUser.pools
        ?.filter((p) => p.type === "over-under-wins")
        .sort((a, b) => b.year - a.year)[0];
      dispatch(updateOverUnders({ ...overUnders, currentPool: pool }));

      // Respect the user's last-used league if it's still in their league list
      const storedId = localStorage.getItem(LEAGUE_PREF_KEY);
      const storedLeagueId = storedId ? parseInt(storedId, 10) : null;
      const preferredLeague = storedLeagueId
        ? dbUser.leagues.find((l) => l.league.leagueId === storedLeagueId)
        : null;
      const currentLeagueId =
        preferredLeague?.league.leagueId ??
        (dbUser.leagues.length > 0 ? dbUser.leagues[0].league.leagueId : undefined);

      const updatedProfile = {
        ...profile,
        owner: dbUser,
        currentLeagueId,
        authSynchronized: true,
        authUser: user,
        authError: undefined,
      };

      console.log("[synchronizeAuth0WithDbLogin] Dispatching updated profile:", updatedProfile);
      dispatch(updateLoginInfo(updatedProfile));

      try {
        await ChatClient.connect(
          {
            id: `${dbUser.ownerId}`,
            name: dbUser.displayName,
            role: "admin",
            image: user?.picture,
          },
          dbUser.streamToken,
        );
        dispatch(setChatConnected(true));
      } catch (chatErr: any) {
        console.error("[synchronizeAuth0WithDbLogin] Chat connect failed:", chatErr);
      }
    } catch (err: any) {
      // Render may be cold-starting (takes up to 45s) or API is down — surface so user sees retry UI instead of infinite spinner
      console.error("[synchronizeAuth0WithDbLogin] API failed:", err);
      dispatch(updateLoginInfo({ ...profile, authError: err?.message ?? "Login failed" }));
    }
  };

/**
 * Seeds a synthetic demo identity for the public read-only demo — the Auth0-free
 * equivalent of synchronizeAuth0WithDbLogin. Fetches the demo owner/league from the
 * anonymous /demo/bootstrap endpoint and marks the profile synchronized so the demo
 * screens render. No Stream chat (demo has no real user).
 */
export const bootstrapDemoSession =
  () => async (dispatch: Function, getState: () => RootState) => {
    const { profile } = getState();
    try {
      const dbUser = await GeneralApiSvc.getDemoBootstrap();
      const currentLeagueId =
        dbUser.leagues.length > 0 ? dbUser.leagues[0].league.leagueId : undefined;

      dispatch(
        updateLoginInfo({
          ...profile,
          owner: dbUser,
          currentLeagueId,
          authSynchronized: true,
          authUser: { sub: "demo|fanpools" } as User,
          authError: undefined,
        }),
      );
    } catch (err: any) {
      dispatch(
        updateLoginInfo({
          ...profile,
          authError: err?.message ?? "Demo failed to load.",
        }),
      );
    }
  };

export const updateCurrentLeague =
  (leagueId: number, currentRoute: string, user: User) =>
  async (dispatch: Function, getState: () => RootState) => {
    const { profile } = getState();
    const newProfile = { ...profile };
    console.log("updating current league to", leagueId);
    const newCurrentLeague = newProfile.owner.leagues.find(
      (l) => l.league.leagueId === leagueId,
    );
    dispatch(
      updateLoginInfo({
        ...newProfile,
        currentLeagueId: newCurrentLeague?.league.leagueId,
      }),
    );
    console.log("updateCurrentLeague checking route", currentRoute);
    if (currentRoute == "/auction") dispatch(getInitialAuctionData(user.sub, true));
    if (currentRoute == "/league-home") dispatch(loadDashboardData());
  };
